import { IDispatchId } from '../../utils/helpers/redux.helper';
import {
  IEntityNetworkPayload,
  IEntityTypePayload,
  IListFilterPayload,
} from '../entity-data/entity-data.actions';
import { EstimateStatusesEnum } from './estimate-data.data';
import { EntityTypesEnum } from '../../app.enum';
import { Estimate } from './estimate.model';
import { ISetEditingDocument } from '../document-data/document-data.actions';
import { List } from 'immutable';

// common interfaces
export interface IEstimatePayload {
  estimate: Estimate;
}

export interface IEstimateListPayload {
  estimates: List<Estimate>;
}

export interface IEstimateStatusPayload {
  status: EstimateStatusesEnum;
}

// action payload interfaces
export interface IMarkSelectedEstimatesAsDone
  extends IEntityTypePayload,
    IEstimateStatusPayload,
    IListFilterPayload {}

export interface IMarkEstimateAsDone
  extends IDispatchId,
    IMarkSelectedEstimatesAsDone,
    IEstimatePayload,
    ISetEditingDocument {}

export interface IBatchMarkEstimateAsDone
  extends IMarkSelectedEstimatesAsDone,
    IEstimateListPayload {}

export interface IBatchMarkEstimateAsDoneNetworkResult
  extends IEntityTypePayload,
    IEstimateListPayload,
    IEstimateStatusPayload {}

export interface IShowJobDialogPayload extends IEntityTypePayload {
  clientId: string;
  documentId: string;
}

// action enums
export enum ActionsEnum {
  MARK_ESTIMATE_AS_DONE = 'MARK_ESTIMATE_AS_DONE',
  MARK_ESTIMATE_AS_DONE_SUCCESS = 'MARK_ESTIMATE_AS_DONE_SUCCESS',
  MARK_ESTIMATE_AS_DONE_ERROR = 'MARK_ESTIMATE_AS_DONE_ERROR',
  MARK_SELECTED_ESTIMATES_AS_DONE = 'MARK_SELECTED_ESTIMATES_AS_DONE',
  BATCH_MARK_ESTIMATE_AS_DONE_STORE = 'BATCH_MARK_ESTIMATE_AS_DONE_STORE',
  BATCH_MARK_ESTIMATE_AS_DONE_NETWORK = 'BATCH_MARK_ESTIMATE_AS_DONE_NETWORK',
  BATCH_MARK_ESTIMATE_AS_DONE_NETWORK_SUCCESS = 'BATCH_MARK_ESTIMATE_AS_DONE_NETWORK_SUCCESS',
  BATCH_MARK_ESTIMATE_AS_DONE_NETWORK_ERROR = 'BATCH_MARK_ESTIMATE_AS_DONE_NETWORK_ERROR',
  ENABLE_JOB_DIALOG = 'ENABLE_JOB_DIALOG',
  DISABLE_JOB_DIALOG = 'DISABLE_JOB_DIALOG',
}

// action definitions
export interface MarkEstimateAsDone {
  type: ActionsEnum.MARK_ESTIMATE_AS_DONE;
  payload: IMarkEstimateAsDone;
}

export interface MarkEstimateAsDoneSuccess {
  type: ActionsEnum.MARK_ESTIMATE_AS_DONE_SUCCESS;
  payload: IEntityNetworkPayload;
}

export interface MarkEstimateAsDoneError {
  type: ActionsEnum.MARK_ESTIMATE_AS_DONE_ERROR;
  payload: IEntityNetworkPayload;
}

export interface MarkSelectedEstimatesAsDone {
  type: ActionsEnum.MARK_SELECTED_ESTIMATES_AS_DONE;
  payload: IMarkSelectedEstimatesAsDone;
}

export interface BatchMarkEstimateAsDoneStore {
  type: ActionsEnum.BATCH_MARK_ESTIMATE_AS_DONE_STORE;
  payload: IBatchMarkEstimateAsDone;
}

export interface BatchMarkEstimateAsDoneNetwork {
  type: ActionsEnum.BATCH_MARK_ESTIMATE_AS_DONE_NETWORK;
  payload: IBatchMarkEstimateAsDone;
}

export interface BatchMarkEstimateAsDoneNetworkSuccess {
  type: ActionsEnum.BATCH_MARK_ESTIMATE_AS_DONE_NETWORK_SUCCESS;
  payload: IBatchMarkEstimateAsDoneNetworkResult;
}

export interface BatchMarkEstimateAsDoneNetworkError {
  type: ActionsEnum.BATCH_MARK_ESTIMATE_AS_DONE_NETWORK_ERROR;
  payload: IBatchMarkEstimateAsDoneNetworkResult;
}

export interface EnableJobDialog {
  type: ActionsEnum.ENABLE_JOB_DIALOG;
  payload: IShowJobDialogPayload;
}

export interface DisableJobDialog {
  type: ActionsEnum.DISABLE_JOB_DIALOG;
  payload: IEntityTypePayload;
}

// action creators
export const markEstimateAsDone = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  estimate: Estimate,
  status: EstimateStatusesEnum,
  setEditingDocument: boolean,
  listFilter?: string
): MarkEstimateAsDone => ({
  type: ActionsEnum.MARK_ESTIMATE_AS_DONE,
  payload: {
    dispatchId,
    entityType,
    estimate,
    status,
    setEditingDocument,
    listFilter,
  },
});

export const markEstimateAsDoneSuccess = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  entity: any
): MarkEstimateAsDoneSuccess => ({
  type: ActionsEnum.MARK_ESTIMATE_AS_DONE_SUCCESS,
  payload: {
    dispatchId,
    entityType,
    entity,
  },
});

export const markEstimateAsDoneError = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  entity: any
): MarkEstimateAsDoneError => ({
  type: ActionsEnum.MARK_ESTIMATE_AS_DONE_ERROR,
  payload: {
    dispatchId,
    entityType,
    entity,
  },
});

export const markSelectedEstimatesAsDone = (
  entityType: EntityTypesEnum,
  status: EstimateStatusesEnum,
  listFilter?: string
): MarkSelectedEstimatesAsDone => ({
  type: ActionsEnum.MARK_SELECTED_ESTIMATES_AS_DONE,
  payload: {
    entityType,
    status,
    listFilter,
  },
});

export const batchMarkEstimateAsDoneStore = (
  entityType: EntityTypesEnum,
  estimates: List<Estimate>,
  status: EstimateStatusesEnum,
  listFilter?: string
): BatchMarkEstimateAsDoneStore => ({
  type: ActionsEnum.BATCH_MARK_ESTIMATE_AS_DONE_STORE,
  payload: {
    entityType,
    estimates,
    status,
    listFilter,
  },
});

export const batchMarkEstimateAsDoneNetwork = (
  entityType: EntityTypesEnum,
  estimates: List<Estimate>,
  status: EstimateStatusesEnum,
  listFilter?: string
): BatchMarkEstimateAsDoneNetwork => ({
  type: ActionsEnum.BATCH_MARK_ESTIMATE_AS_DONE_NETWORK,
  payload: {
    entityType,
    estimates,
    status,
    listFilter,
  },
});

export const batchMarkEstimateAsDoneNetworkSuccess = (
  entityType: EntityTypesEnum,
  estimates: List<Estimate>,
  status: EstimateStatusesEnum
): BatchMarkEstimateAsDoneNetworkSuccess => ({
  type: ActionsEnum.BATCH_MARK_ESTIMATE_AS_DONE_NETWORK_SUCCESS,
  payload: {
    entityType,
    estimates,
    status,
  },
});

export const batchMarkEstimateAsDoneNetworkError = (
  entityType: EntityTypesEnum,
  estimates: List<Estimate>,
  status: EstimateStatusesEnum
): BatchMarkEstimateAsDoneNetworkError => ({
  type: ActionsEnum.BATCH_MARK_ESTIMATE_AS_DONE_NETWORK_ERROR,
  payload: {
    entityType,
    estimates,
    status,
  },
});

export const enableJobDialog = (
  entityType: EntityTypesEnum,
  clientId: string,
  documentId: string
): EnableJobDialog => ({
  type: ActionsEnum.ENABLE_JOB_DIALOG,
  payload: {
    entityType,
    clientId,
    documentId
  },
});

export const disableJobDialog = (
  entityType: EntityTypesEnum,
): DisableJobDialog => ({
  type: ActionsEnum.DISABLE_JOB_DIALOG,
  payload: {
    entityType,    
  },
});
